import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { autobind } from 'core-decorators'
import { observer, inject } from 'mobx-react'
import { Link } from 'react-router'

import Button from '../../shared/components/Button'
import TextInput from '../../shared/components/TextInput'
import SelectInput from '../../shared/components/SelectInput'
import ValidationInfo from '../../shared/components/ValidationInfo'

import { PassThroughActions } from '../../shared/decorators/form/actions'

@inject('auth', 'user') @observer
@PassThroughActions
class Form extends Component {

  static propTypes = {
    auth: PropTypes.object.isRequired,
    current: PropTypes.object.isRequired,
    isBusy: PropTypes.bool,
    mayEditBaseData: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
  }

  // eslint-disable-next-line complexity
  constructor(props) {
    super(props)

    this.SUPERADMIN_ID = window.APP_CONFIG.groups
      .find((group) => group.name === 'Superadministrator')
      .id

    this.ADMIN_ID = window.APP_CONFIG.groups
      .find((group) => group.name === 'Administrator')
      .id

    this.MUSEUM_ID = window.APP_CONFIG.groups
      .find((group) => group.name === 'Museum')
      .id

    const groupIds = {
      [['SUPERADMIN', 'ADMIN', 'USER'].sort().join(',')]: this.SUPERADMIN_ID,
      [['ADMIN', 'USER'].sort().join(',')]: this.ADMIN_ID,
      [['USER'].sort().join(',')]: this.MUSEUM_ID,
    }

    let forcedDistrictId = null

    if (!props.auth.acs.isAuthorized('SUPERADMIN')) {
      forcedDistrictId = props.auth.user.districtId
    }

    let data = {}

    if (this.props.current) {

      data = {
        ...this.props.current.asJSON,
        districtId: this.props.current.districtId,
        museumId: this.props.current.museumId,
      }

      const permissions = Object.keys(data.permissions).sort().join(',')

      data.group = groupIds[permissions || 'USER']

      if (data.museum) {
        data.museumId = data.museum.id
        data.museum = data.museum.name
        data.museumIneditable = !!data.museum
      }

      if (data.districtId || data.group === this.SUPERADMIN_ID) {
        data.districtIneditable = true
      }

      data.groupIneditable = !!permissions

      if (data.identification) {
        data.login = data.identification.login
        // delete data.identification
      }

    }

    if (forcedDistrictId) {
      data.districtId = forcedDistrictId
      data.districtIneditable = true
    }

    data.address = data.address || 'Herr'

    this.state = data
    this.props.current.set(data)
  }

  @autobind
  handleChange({ target }) {

    let data = {
      // make sure this is always set
      address: this.state.address,
      [target.name]: target.value,
    }

    if (target.name === 'group') {
      data.districtIdDisabled = target.value * 1 === this.SUPERADMIN_ID
        && target.checked
      data.museumDisabled = target.value * 1 !== this.MUSEUM_ID
        && target.checked
      if (data.museumDisabled) {
        data.museum = ''
      }
    }

    if (
      target.name === 'login'
      || target.name === 'password'
      || target.name === 'passwordConfirm'
    ) {
      data = {
        // for the local state
        ...data,

        // for the model
        identification: {
          ...this.state.identification,
          ...data,
        }
      }
    }
    else if (this.state.districtIneditable === true) {
      data.districtId = this.state.districtId
    }

    this.setState(data)
    this.props.current.set(data)

  }

  @autobind
  handleDelete(e) {
    e.preventDefault()
    if (this.props.onDelete) {
      this.props.onDelete(this.props.current)
    }
  }

  @autobind
  handleCancel(e) {
    e.preventDefault()
    if (this.props.onCancel) {
      this.props.onCancel(this.props.current)
    }
  }

  @autobind
  handleSave(e) {
    e.preventDefault()

    const data = { ...this.state }
    data.identification = {
      login: data.login,
      password: data.password,
      passwordConfirm: data.passwordConfirm,
    }
    data.museum = {
      name: data.museum,
    }

    delete data.login
    delete data.password
    delete data.districtIdDisabled
    delete data.districtIneditable
    delete data.groupIneditable
    delete data.museumDisabled
    delete data.museumIneditable

    if (data.id) {
      delete data.museum

      if (!data.password) {
        delete data.password
        delete data.passwordConfirm
      }
    }

    if (this.props.onSave) {
      this.props.onSave(this.props.current)
    }

  }

  // eslint-disable-next-line complexity
  render() {

    const {
      auth,
      mayEditBaseData,
    } = this.props

    const {
      address,
      districtId,
      districtIdDisabled,
      districtIneditable,
      email,
      firstname,
      group,
      groupIneditable,
      id,
      login,
      museum,
      museumId,
      museumDisabled,
      museumIneditable,
      password,
      passwordConfirm,
      surname,
    } = this.state
    const { current: { isValid, validFields } } = this.props

    const { groups } = window.APP_CONFIG

    const groupIsMuseum = group * 1 === this.MUSEUM_ID

    return (
      <div className="user container">
        <form
          className="form form-horizontal vertical"
          onSubmit={this.handleSave}
          onChange={this.handleChange}
          autoComplete="off"
        >

          <div className="form-group">
            <label
              className="col-xs-4"
              htmlFor="user-groups"
            >
              Typ
            </label>

            <div className="col-xs-8">

              {groups
                .filter((groupDef) => !groupDef.permissions.ADMIN || auth.acs.isAuthorized('SUPERADMIN'))
                .map((groupDef) => {
                  return (
                    <div
                      className="radio"
                      key={groupDef.id}
                    >
                      <label>
                        <input
                          type="radio"
                          name="group"
                          dataCy={`user.form.group-${groupDef.id}`}
                          value={groupDef.id}
                          checked={group * 1 === groupDef.id}
                          onChange={this.handleChange}
                          disabled={groupIneditable || !mayEditBaseData}
                        />
                        <span>{groupDef.name}</span>
                      </label>
                    </div>
                  )
                })}

              <ValidationInfo isValid={validFields.group} />

            </div>

          </div>

          {groupIsMuseum
            ? (
              <TextInput
                name="museum"
                value={museum}
                label="Museumsname"
                isValid={validFields.museum}
                required
                size={8}
                disabled={
                  museumIneditable || museumDisabled || !mayEditBaseData
                }
                helpText={
                  museumId
                    ? <Link to={`/museum/${museumId}/edit`}>Museum bearbeiten</Link>
                    : null
                }
                autoComplete="off"
                dataCy="user.form.is-museum"
              />
            )
            : null}

          <hr />

          {!districtIdDisabled
            ? (
              <SelectInput
                size={4}
                name="districtId"
                value={districtId || ''}
                label="Bezirk"
                isValid={validFields.districtId}
                required
                onChange={this.handleChange}
                disabled={districtIneditable || !mayEditBaseData}
                options={[
                  { value: '', title: '-- Bezirk wählen --' },
                  ...window.APP_CONFIG.districts.map((district) => ({ value: district.id, title: district.name }))
                ]}
                dataCy="user.form.district-id"
              />
            )
            : (
              <div className="form-group">
                <label
                  className="col-xs-4"
                  htmlFor="districtId"
                >
                  Bezirk
                </label>
                <div className="col-xs-8">
                  <span>Alle</span>
                </div>
              </div>
            )}

          <hr />

          <SelectInput
            size={2}
            name="address"
            value={address || ''}
            label="Anrede"
            isValid={validFields.address}
            required
            disabled={!mayEditBaseData}
            onChange={this.handleChange}
            options={[
              { value: 'Herr', title: 'Herr' },
              { value: 'Frau', title: 'Frau' },
            ]}
          />

          <TextInput
            size={4}
            name="firstname"
            value={firstname}
            label="Vorname"
            disabled={!mayEditBaseData}
            isValid={validFields.firstname}
            required
            autoComplete="off"
          />

          <TextInput
            size={4}
            name="surname"
            value={surname}
            label="Nachname"
            disabled={!mayEditBaseData}
            isValid={validFields.surname}
            required
            autoComplete="off"
          />

          <TextInput
            size={6}
            name="email"
            value={email}
            label="E-Mail"
            disabled={!mayEditBaseData}
            isValid={validFields.email}
            required
            autoComplete="off"
          />

          <hr />

          <TextInput
            size={4}
            name="login"
            value={login}
            label="Benutzername"
            disabled={!mayEditBaseData}
            isValid={validFields.identification.login || validFields.identification}
            required
            autoComplete="off"
          />

          <TextInput
            size={4}
            type="password"
            name="password"
            value={password}
            label="Passwort"
            autoComplete="new-password"
            isValid={validFields.identification.password || validFields.identification}
          />

          <div className="form-group">
            <label
              className="col-xs-4"
              htmlFor="passwordConfirm"
            >
              Passwort bestätigen
            </label>
            <div className="col-xs-4">
              <input
                autoComplete="new-password"
                type="password"
                className="form-control"
                id="passwordConfirm"
                name="passwordConfirm"
                value={passwordConfirm}
              />
            </div>
          </div>

          <div className="form-group">
            <Button
              className="action btn btn-danger pull-left"
              type="button"
              onClick={this.handleDelete}
              disabled={!id || !mayEditBaseData}
              icon="trash"
              busy={this.props.isBusy}
              confirmation="Diesen Nutzer wirklich löschen?"
            >
              Löschen
            </Button>

            <Button
              className="btn modal-cancel btn-default"
              type="button"
              onClick={this.handleCancel}
            >
              Abbrechen
            </Button>

            <Button
              className="btn btn-primary modal-submit pull-right"
              type="submit"
              onClick={this.handleSave}
              disabled={!isValid}
              icon="ok"
              busy={this.props.isBusy}
            >
              Speichern
            </Button>
          </div>

        </form>
      </div>
    )
  }
}

export { Form as default }



// WEBPACK FOOTER //
// ./src/user/components/Form.js